<script setup>
import { computed, ref, watch } from "vue";
import SimpleTable from "@/components/table/SimpleTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import VehicleAvatar from "@/components/VehicleAvatar.vue";
import CopyValue from "@/components/CopyValue.vue";
import { vehicleStore } from "@/_store/vehicleStore";

const props = defineProps({
  date: {
    type: String,
    default: "",
  },
  selectedVehicleIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "prev",
  "next",
  "toggle-vehicle",
  "loading-change",
  "sync-selected-vehicles",
]);

const vehiclesStore = vehicleStore();
const loadingVehicles = ref(false);
const vehicleRequestToken = ref(0);
const activeVehicles = ref([]);

const vehicleHeaders = Object.freeze([
  { header: "Vehículo", field: "name", sort: false, position: "start" },
  {
    header: "Matrícula",
    field: "registration_number",
    sort: false,
    position: "start",
  },
]);

const vehicleRows = computed(() => {
  const rows = activeVehicles.value.map((vehicle) => {
    const id = extractVehicleId(vehicle);

    return {
      _id: id,
      name: vehicle?.name || vehicle?.label || "Vehiculo",
      model: vehicle?.model || "",
      abbreviation: vehicle?.abbreviation || "",
      route_color_hex: vehicle?.route_color_hex || vehicle?.color || "#5baee9",
      registration_number: vehicle?.registration_number || vehicle?.plate || "",
    };
  });

  return {
    total: rows.length,
    data: rows,
  };
});

function onToggleVehicle(vehicleId, checked) {
  emit("toggle-vehicle", vehicleId, checked);
}

function onSelectedVehicleIdsUpdated(selectedIds) {
  emit("sync-selected-vehicles", selectedIds);
}

function onToggleCheckItem(payload) {
  onToggleVehicle(payload?.rowId, payload?.checked);
}

function extractVehicleId(vehicle) {
  return String(vehicle?._id || "");
}

function syncSelectedVehiclesFromActiveList() {
  const ids = activeVehicles.value
    .map((vehicle) => extractVehicleId(vehicle))
    .filter(Boolean);

  emit("sync-selected-vehicles", [...new Set(ids)]);
}

function setLoadingState(value) {
  loadingVehicles.value = value;
  emit("loading-change", value);
}

function loadActiveVehicles(date) {
  const currentRequest = ++vehicleRequestToken.value;
  const normalizedDate = String(date || "").trim();

  if (!normalizedDate) {
    activeVehicles.value = [];
    emit("sync-selected-vehicles", []);
    setLoadingState(false);
    return;
  }

  setLoadingState(true);

  vehiclesStore
    .getActiveVehicles(normalizedDate)
    .then(() => {
      if (currentRequest !== vehicleRequestToken.value) return;

      activeVehicles.value = Array.isArray(vehiclesStore.activeVehicles)
        ? [...vehiclesStore.activeVehicles]
        : [];

      syncSelectedVehiclesFromActiveList();
    })
    .finally(() => {
      if (currentRequest === vehicleRequestToken.value) {
        setLoadingState(false);
      }
    });
}

watch(
  () => props.date,
  (value) => {
    loadActiveVehicles(value);
  },
  {
    immediate: true,
  }
);
</script>

<template>
  <div
    class="multisteps-form__panel js-active p-2 position-relative bg-white rounded-3"
    data-animation="FadeIn"
  >
    <div class="d-flex flex-column" style="min-height: 300px">
      <div class="text-center mb-3">
        <p class="mb-0">Selecciona los vehículos del plan</p>
      </div>
      <div class="mb-3">
        <simple-table
          :headers="vehicleHeaders"
          :items="vehicleRows"
          :show-check-input="true"
          :selected-ids="selectedVehicleIds"
          :disable-check-input="loadingVehicles"
          :empty-message="'No hay vehiculos disponibles para la fecha seleccionada'"
          :show-line-separator="true"
          :height="270"
          @update:selected-ids="onSelectedVehicleIdsUpdated"
          @toggle-check-item="onToggleCheckItem"
        >
          <template #default="{ item }">
            <argon-td
              :text-primary="item.name"
              :text-secondary="item.model"
              position="start"
            >
              <template #prepend>
                <vehicle-avatar
                  :label="item.name"
                  :abbreviation="item.abbreviation"
                  :accent-color="item.route_color_hex"
                  :size="26"
                />
              </template>
            </argon-td>
            <argon-td
              :text-primary="item.registration_number"
              position="start"
            >
              <template v-if="item.registration_number" #append>
                <copy-value :value-to-copy="item.registration_number" size="xs" show-on-hover />
              </template>
            </argon-td>
          </template>
        </simple-table>
      </div>

      <div class="d-flex mt-auto">
        <argon-button
          color="primary"
          variant="outline"
          size="xs"
          aria-label="Anterior"
          class="mb-0 btn-icon-only"
          @click.prevent="emit('prev')"
        >
          <span class="btn-inner--icon">
            <i class="fas fa-chevron-left" aria-hidden="true"></i>
          </span>
        </argon-button>
        <argon-button
          class="mb-0 ms-auto btn-icon-only"
          color="primary"
          variant="outline"
          size="xs"
          :disabled="loadingVehicles"
          aria-label="Siguiente"
          @click.prevent="emit('next')"
        >
          <span class="btn-inner--icon">
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </span>
        </argon-button>
      </div>
    </div>
  </div>
</template>
